import { ref, useContext, readonly } from '@nuxtjs/composition-api';
import { Logger } from '@wemade-vsf/core';
import type { Ref } from '@nuxtjs/composition-api';
import type { FittingListEntry, Part, Brand, Model } from '../../types';

interface UsePartsBySkuInterface {
  loading: Readonly<Ref<boolean>>;
  error: Readonly<Ref<{
    load: Error | null
  }>>;

  load(): Promise<FittingListEntry[]>;
}
export function usePartsBySku (sku: string): UsePartsBySkuInterface {
  const loading = ref(false);
  const error = ref({
    load: null
  });

  const { app } = useContext();

  async function load(): Promise<FittingListEntry[]> {
    Logger.debug('usePartsBySku/load', sku);
    let result = [];
    loading.value = true;
    try {
      const { data } = await app.$vsf.$magento.api.pfPartsBySku({ sku, pageSize: 2000, currentPage: 1 });
      Logger.debug('[Result]: ', data);
      if (data?.WmPartFinderParts?.items?.length) {
        result = await buildFittingList(data.WmPartFinderParts.items);
      }
    } catch (err) {
      error.value.load = err;
      Logger.error('usePartsBySku/load', err);
    } finally {
      loading.value = false;
    }

    return result;
  }

  async function buildFittingList(items: Part[]): Promise<FittingListEntry[]> {
    const modelIds = new Set([])
    const brandIds = new Set([]);
    items.forEach(i => {
      modelIds.add(i.model_id);
      brandIds.add(i.brand_id);
    })

    const brands = await loadBrands(brandIds);
    const models = await loadModels(modelIds);

    const entries = new Map();

    items.forEach(item => {
      const key = `${item.brand_id}-${item.model_id}`;
      if (!entries.has(key)) {
        const brand = brands.find(b => b.brand_id === item.brand_id);
        const model = models.find(m => m.model_id === item.model_id);
        if (!brand || !model) return;
        
        let years = new Set([]);
        if (item.from_year === item.to_year) {
          years.add(item.from_year);
        } else {
          for (let i = item.from_year, y = item.to_year; i <= y; i++) {
            years.add(i);
          }
        }       
        entries.set(key, {
          brand,
          model,
          years
        })
      } else {
        const entry = entries.get(key);
        if (item.from_year === item.to_year) {
          entry.years.add(item.from_year);
        } else {
          for (let i = item.from_year, y = item.to_year; i <= y; i++) {
            entry.years.add(i);
          }
        }
      }
    });
    const result = [...entries.values()].sort((a, b) => {
      return a.brand.name === b.brand.name
        ? (a.model.name > b.model.name ? 1 : -1)
        : (a.brand.name > b.brand.name ? 1 : -1)
    }).map(item => {
      // @ts-ignore
      const years: number[] = Array.from(item.years).sort();
      const ranges = [];
      if (years.length > 0) {
        let currentFrom: number = years[0];
        let currentTo: number = years[0];

        for (let i = 1, j = years.length; i < j; i++) {
          if (years[i] === currentTo + 1) {
            currentTo = years[i];
            continue;
          }
          ranges.push([currentFrom, currentTo])
          currentFrom = years[i];
          currentTo = years[i]
        }
        ranges.push([currentFrom, currentTo]);
      }
      item.years = ranges.map(r => {
        return r[0] === r[1] ? `${r[0]}` : `${r[0]} - ${r[1]}`;
      });
      return item;
    });

    return result
  }

  async function loadBrands(brandIds: Set<number>): Promise<Brand[]> {
    const { data } = await app.$vsf.$magento.api.pfBrands({ pageSize: 1000, currentPage: 1});
    return data?.WmPartFinderBrands?.items?.length
      ? data.WmPartFinderBrands.items.filter(b => brandIds.has(b.brand_id))
      : [];
  }

  async function loadModels(modelIds: Set<number>): Promise<Model[]> {
    const { data } = await app.$vsf.$magento.api.pfModels({
      filter: {
        model_id: { in: [...modelIds].map(m => `${m}`) }
      },
      pageSize: modelIds.size,
      currentPage: 1
    });

    return data?.WmPartFinderModels?.items?.length
      ? data.WmPartFinderModels.items
      : []
  }

  return {
    loading: readonly(loading),
    error: readonly(error),
    load
  }
}